import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Search, 
  MapPin, 
  Star, 
  Scissors,
  User,
  Filter,
  Heart,
  Calendar,
  ArrowLeft,
  Map,
  List,
  X,
  Phone,
  Navigation,
  Mail,
  UserPlus,
  Send,
  ExternalLink,
  Globe,
  Users,
  MessageCircle,
  Share2,
  Shield
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import BetaInviteSystem from "@/components/BetaInviteSystem";
import MobileBottomNav from "@/components/MobileBottomNav";

// Set Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const PROFESSIONAL_TYPES = [
  { value: "all", label: "All Professionals" },
  { value: "barber", label: "Barbers" },
  { value: "stylist", label: "Hair Stylists" },
  { value: "colorist", label: "Colorists" },
  { value: "braider", label: "Braiders" }
];

const YELP_CATEGORIES = [
  { value: "barbers", label: "Barbers" },
  { value: "hair", label: "Hair Salons" },
  { value: "hairstylists", label: "Hair Stylists" },
  { value: "braiders", label: "Braiders" }
];

const FindStylists = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stylists, setStylists] = useState([]);
  const [filteredStylists, setFilteredStylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [selectedStylist, setSelectedStylist] = useState(null);
  
  // Search mode: "myhairchair" or "yelp"
  const [searchMode, setSearchMode] = useState("myhairchair");
  
  // Yelp search state
  const [yelpResults, setYelpResults] = useState([]);
  const [yelpLoading, setYelpLoading] = useState(false);
  const [yelpLocation, setYelpLocation] = useState("");
  const [yelpCategory, setYelpCategory] = useState("barbers");
  const [yelpSearchTerm, setYelpSearchTerm] = useState(""); // Search by name
  const [yelpTotal, setYelpTotal] = useState(0);
  
  // Invite stylist modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [inviteSending, setInviteSending] = useState(false);
  
  // Map refs
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  
  // Get initial type from URL query param
  const initialType = searchParams.get("type") || "all";
  
  const [filters, setFilters] = useState({
    search: "",
    city: "",
    state: "",
    zipCode: "",
    professionalType: initialType
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchStylists();
    if (token) {
      fetchFavorites();
    }
  }, [token]);

  useEffect(() => {
    applyFilters();
  }, [stylists, filters]);

  // Initialize map when switching to map view
  useEffect(() => {
    if (viewMode === "map" && mapContainer.current && !map.current) {
      // Atlanta center coordinates
      const center = [-84.3880, 33.7490];
      
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: center,
        zoom: 10
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      
      // Add markers once map loads
      map.current.on('load', () => {
        addMarkersToMap();
      });
    }
    
    return () => {
      // Don't destroy map on unmount to avoid re-initialization issues
    };
  }, [viewMode]);

  // Update markers when filtered stylists change
  useEffect(() => {
    if (map.current && map.current.loaded()) {
      addMarkersToMap();
    }
  }, [filteredStylists, viewMode]);

  const addMarkersToMap = () => {
    // Remove existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add markers for each stylist with coordinates
    filteredStylists.forEach(stylist => {
      if (stylist.coordinates?.lat && stylist.coordinates?.lng) {
        // Create custom marker element
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.style.cssText = `
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
          cursor: pointer;
          border: 3px solid white;
        `;
        el.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <circle cx="6" cy="6" r="3"></circle>
            <path d="M8.12 8.12L12 12"></path>
            <path d="M20 4L8.12 15.88"></path>
            <circle cx="6" cy="18" r="3"></circle>
            <path d="M14.8 14.8L18 18"></path>
          </svg>
        `;

        // Create popup content
        const popupContent = `
          <div style="padding: 12px; max-width: 250px; font-family: system-ui;">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 48px; height: 48px; background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div>
                <h3 style="margin: 0; font-weight: 700; font-size: 16px; color: #1f2937;">${stylist.user?.name || 'Stylist'}</h3>
                <p style="margin: 0; color: #f59e0b; font-size: 14px; font-weight: 500;">${stylist.professional_type || 'Stylist'}</p>
              </div>
            </div>
            ${stylist.rating ? `
              <div style="display: flex; align-items: center; gap: 4px; margin-bottom: 8px;">
                <span style="color: #f59e0b;">★</span>
                <span style="font-weight: 600;">${stylist.rating.toFixed(1)}</span>
                <span style="color: #6b7280; font-size: 13px;">(${stylist.total_reviews || 0} reviews)</span>
              </div>
            ` : ''}
            ${stylist.city ? `
              <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 13px; display: flex; align-items: center; gap: 4px;">
                📍 ${stylist.city}, ${stylist.state || 'GA'}
              </p>
            ` : ''}
            ${stylist.specialties?.length > 0 ? `
              <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 12px;">
                ${stylist.specialties.slice(0, 3).map(s => `
                  <span style="background: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 12px; font-size: 11px;">${s}</span>
                `).join('')}
              </div>
            ` : ''}
            <div style="display: flex; gap: 8px;">
              <button onclick="window.location.href='/book-guest/${stylist.user_id}'" style="flex: 1; padding: 8px; background: white; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; font-size: 13px;">
                View Profile
              </button>
              <button onclick="window.location.href='/book-guest/${stylist.user_id}'" style="flex: 1; padding: 8px; background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%); color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 13px;">
                Book Now
              </button>
            </div>
          </div>
        `;

        const popup = new mapboxgl.Popup({ offset: 25, maxWidth: '300px' })
          .setHTML(popupContent);

        const marker = new mapboxgl.Marker(el)
          .setLngLat([stylist.coordinates.lng, stylist.coordinates.lat])
          .setPopup(popup)
          .addTo(map.current);

        markers.current.push(marker);
      }
    });

    // Fit map to markers if there are any
    if (markers.current.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      markers.current.forEach(marker => {
        bounds.extend(marker.getLngLat());
      });
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 12 });
    }
  };

  const fetchStylists = async () => {
    try {
      const response = await axios.get(`${API}/stylists/`);
      setStylists(response.data || []);
    } catch (error) {
      console.error("Failed to fetch stylists:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/clients/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data?.favorites || []);
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
    }
  };

  const applyFilters = () => {
    let results = [...stylists];

    // Search by name
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(s => 
        s.user?.name?.toLowerCase().includes(searchLower) ||
        s.specialties?.some(spec => spec.toLowerCase().includes(searchLower))
      );
    }

    // Filter by city
    if (filters.city) {
      const cityLower = filters.city.toLowerCase();
      results = results.filter(s => s.city?.toLowerCase().includes(cityLower));
    }

    // Filter by state
    if (filters.state) {
      const stateLower = filters.state.toLowerCase();
      results = results.filter(s => s.state?.toLowerCase().includes(stateLower));
    }

    // Filter by zip code
    if (filters.zipCode) {
      results = results.filter(s => s.zip_code?.includes(filters.zipCode));
    }

    // Filter by professional type
    if (filters.professionalType && filters.professionalType !== "all") {
      const typeFilter = filters.professionalType.toLowerCase();
      
      const barberSpecialties = ["fade", "fades", "beard", "classic cuts", "line-up", "razor", "shave", "taper", "barber"];
      const stylistSpecialties = ["color", "highlights", "balayage", "styling", "blowout", "cuts", "updo"];
      const coloristSpecialties = ["color", "highlights", "balayage", "ombre", "dye"];
      const braiderSpecialties = ["braids", "locs", "twist", "cornrow", "protective"];
      
      results = results.filter(s => {
        if (s.professional_type?.toLowerCase() === typeFilter) {
          return true;
        }
        
        const specialties = s.specialties?.map(spec => spec.toLowerCase()) || [];
        
        if (typeFilter === "barber") {
          return specialties.some(spec => barberSpecialties.some(b => spec.includes(b)));
        } else if (typeFilter === "stylist") {
          return specialties.some(spec => stylistSpecialties.some(st => spec.includes(st)));
        } else if (typeFilter === "colorist") {
          return specialties.some(spec => coloristSpecialties.some(c => spec.includes(c)));
        } else if (typeFilter === "braider") {
          return specialties.some(spec => braiderSpecialties.some(br => spec.includes(br)));
        }
        
        return false;
      });
    }

    setFilteredStylists(results);
  };

  const toggleFavorite = async (stylistId) => {
    if (!token) {
      toast.error("Please login to save favorites");
      navigate("/login");
      return;
    }

    const isFavorite = favorites.includes(stylistId);

    try {
      if (isFavorite) {
        await axios.delete(`${API}/clients/favorites/${stylistId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => prev.filter(id => id !== stylistId));
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/clients/favorites`, { stylist_id: stylistId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => [...prev, stylistId]);
        toast.success("Added to favorites!");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      city: "",
      state: "",
      zipCode: "",
      professionalType: "all"
    });
  };

  // Search Yelp for barbers/stylists
  const searchYelp = async () => {
    if (!yelpLocation.trim()) {
      toast.error("Please enter a city or location to search");
      return;
    }

    setYelpLoading(true);
    setYelpResults([]);
    
    try {
      const params = {
        location: yelpLocation.trim(),
        category: yelpCategory,
        limit: 20
      };
      
      // Add search term if provided
      if (yelpSearchTerm.trim()) {
        params.term = yelpSearchTerm.trim();
      }
      
      const response = await axios.get(`${API}/yelp/search`, { params });
      
      setYelpResults(response.data.businesses || []);
      setYelpTotal(response.data.total || 0);
      
      if (response.data.businesses?.length === 0) {
        toast.info("No results found. Try a different location, category, or name.");
      }
    } catch (error) {
      console.error("Yelp search failed:", error);
      toast.error("Failed to search Yelp. Please try again.");
    } finally {
      setYelpLoading(false);
    }
  };

  // Pre-fill invite form from Yelp business
  const inviteYelpBusiness = (business) => {
    setInviteForm({
      name: business.name,
      email: "",
      phone: business.phone?.replace(/[^0-9]/g, "") || "",
      message: `Hi! I found your business on Yelp and would love to book appointments with you through MyHairChair. It's a great platform that helps you manage bookings, accept payments, and grow your client base!`
    });
    setShowInviteModal(true);
  };

  // Share invite via SMS/Text
  const shareViaText = (business = null) => {
    const inviterName = user?.name || "A friend";
    const businessName = business?.name || inviteForm.name || "your business";
    const signupUrl = "https://myhairchair.com/register?role=stylist";
    
    const message = business 
      ? `Hey ${businessName}! ${inviterName} wants to book with you on MyHairChair - a free app for barbers & stylists to manage bookings and get paid. Join here: ${signupUrl}`
      : `Hey! ${inviterName} invited you to join MyHairChair - a free app for barbers & stylists to manage bookings and get paid. Sign up here: ${signupUrl}`;
    
    const phoneNumber = business?.phone?.replace(/[^0-9]/g, "") || inviteForm.phone?.replace(/[^0-9]/g, "") || "";
    
    // Try Web Share API first (works on mobile)
    if (navigator.share) {
      navigator.share({
        title: "Join MyHairChair",
        text: message,
        url: signupUrl
      }).then(() => {
        toast.success("Invite shared!");
      }).catch((err) => {
        // User cancelled or error - fall back to SMS
        if (err.name !== 'AbortError') {
          openSmsLink(phoneNumber, message);
        }
      });
    } else {
      // Fall back to SMS link
      openSmsLink(phoneNumber, message);
    }
  };

  // Open SMS link
  const openSmsLink = (phone, message) => {
    const encodedMessage = encodeURIComponent(message);
    // Use different SMS URL format for iOS vs Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const smsUrl = phone 
      ? (isIOS ? `sms:${phone}&body=${encodedMessage}` : `sms:${phone}?body=${encodedMessage}`)
      : (isIOS ? `sms:&body=${encodedMessage}` : `sms:?body=${encodedMessage}`);
    
    window.open(smsUrl, '_self');
    toast.success("Opening text message...");
  };

  // Send invite to a stylist/barber
  const sendStylistInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error("Please enter the stylist's name and email");
      return;
    }

    setInviteSending(true);
    try {
      await axios.post(`${API}/stylists/invite`, {
        stylist_name: inviteForm.name,
        stylist_email: inviteForm.email,
        stylist_phone: inviteForm.phone,
        personal_message: inviteForm.message,
        invited_by: user?.name || "A MyHairChair User"
      });
      
      toast.success(`Invitation sent to ${inviteForm.name}!`);
      setShowInviteModal(false);
      setInviteForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Failed to send invite:", error);
      toast.error("Failed to send invitation. Please try again.");
    } finally {
      setInviteSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-orange-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-gray-800">MyHairChair</span>
            </Link>
            <div className="flex items-center gap-3">
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                >
                  My Dashboard
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate("/login")}>Sign In</Button>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Page Title */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Find Your Perfect Stylist</h1>
            <p className="text-gray-600">Discover talented barbers and stylists in your area</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Invite Your Stylist Button */}
            <Button
              variant="outline"
              onClick={() => setShowInviteModal(true)}
              className="border-orange-300 text-orange-600 hover:bg-orange-50"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Your Stylist
            </Button>
            
            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm border">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className={viewMode === "list" ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white" : ""}
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
              <Button
                variant={viewMode === "map" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("map")}
                className={viewMode === "map" ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white" : ""}
              >
                <Map className="w-4 h-4 mr-1" />
                Map
              </Button>
            </div>
          </div>
        </div>

        {/* Search Instructions */}
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <p className="text-sm text-amber-800">
            <span className="font-medium">💡 How to search:</span> Search our registered stylists, or use <strong>Search Yelp</strong> to find barbers and stylists anywhere in the US and invite them to join!
          </p>
        </div>

        {/* Search Mode Toggle */}
        <div className="mb-6">
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm border w-fit">
            <Button
              variant={searchMode === "myhairchair" ? "default" : "ghost"}
              size="sm"
              onClick={() => setSearchMode("myhairchair")}
              className={searchMode === "myhairchair" ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white" : ""}
            >
              <Users className="w-4 h-4 mr-2" />
              MyHairChair Stylists
            </Button>
            <Button
              variant={searchMode === "yelp" ? "default" : "ghost"}
              size="sm"
              onClick={() => setSearchMode("yelp")}
              className={searchMode === "yelp" ? "bg-gradient-to-r from-red-500 to-red-600 text-white" : ""}
            >
              <Globe className="w-4 h-4 mr-2" />
              Search Yelp (Nationwide)
            </Button>
          </div>
        </div>

        {/* MyHairChair Search */}
        {searchMode === "myhairchair" && (
          <>
            {/* Search Bar */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Search by name or specialty..."
                      value={filters.search}
                      onChange={(e) => setFilters({...filters, search: e.target.value})}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowFilters(!showFilters)}
                      className={showFilters ? "border-orange-500 text-orange-600" : ""}
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                    </Button>
                    {(filters.city || filters.state || filters.zipCode || filters.professionalType !== "all") && (
                      <Button variant="ghost" onClick={clearFilters} className="text-gray-500">
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expanded Filters */}
                {showFilters && (
                  <div className="grid md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                    <div>
                      <Label className="text-sm text-gray-600">City</Label>
                      <Input
                        placeholder="Enter city..."
                        value={filters.city}
                        onChange={(e) => setFilters({...filters, city: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">State</Label>
                      <Input
                        placeholder="Enter state..."
                        value={filters.state}
                        onChange={(e) => setFilters({...filters, state: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Zip Code</Label>
                      <Input
                        placeholder="Enter zip..."
                        value={filters.zipCode}
                        onChange={(e) => setFilters({...filters, zipCode: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Type</Label>
                      <Select 
                        value={filters.professionalType} 
                        onValueChange={(value) => setFilters({...filters, professionalType: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PROFESSIONAL_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results Count */}
            <div className="mb-4 flex items-center justify-between">
              <p className="text-gray-600">
                Found <span className="font-semibold text-gray-800">{filteredStylists.length}</span> professionals on MyHairChair
              </p>
              {favorites.length > 0 && (
                <Link to="/my-favorites" className="text-orange-600 hover:text-orange-700 text-sm flex items-center gap-1">
                  <Heart className="w-4 h-4 fill-current" />
                  View My Favorites ({favorites.length})
                </Link>
              )}
            </div>
          </>
        )}

        {/* Yelp Search */}
        {searchMode === "yelp" && (
          <>
            <Card className="mb-6 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">Y</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Search Yelp</h3>
                    <p className="text-xs text-gray-500">Find barbers & stylists anywhere in the US by name or location</p>
                  </div>
                </div>
                
                {/* Search by Name */}
                <div className="mb-4">
                  <Label className="text-sm text-gray-600 mb-1 block">Search by Shop/Salon Name (Optional)</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Enter barber shop or salon name..."
                      value={yelpSearchTerm}
                      onChange={(e) => setYelpSearchTerm(e.target.value)}
                      className="pl-10"
                      onKeyDown={(e) => e.key === 'Enter' && searchYelp()}
                    />
                  </div>
                </div>
                
                {/* Location and Category */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <Label className="text-sm text-gray-600 mb-1 block">Location *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        placeholder="Enter city or location (e.g., New York, NY)"
                        value={yelpLocation}
                        onChange={(e) => setYelpLocation(e.target.value)}
                        className="pl-10"
                        onKeyDown={(e) => e.key === 'Enter' && searchYelp()}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 items-end">
                    <div>
                      <Label className="text-sm text-gray-600 mb-1 block">Category</Label>
                      <Select value={yelpCategory} onValueChange={setYelpCategory}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {YELP_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={searchYelp}
                      disabled={yelpLoading}
                      className="bg-red-500 hover:bg-red-600 text-white"
                    >
                      {yelpLoading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Search
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Yelp Results Count */}
            {yelpResults.length > 0 && (
              <div className="mb-4">
                <p className="text-gray-600">
                  Found <span className="font-semibold text-gray-800">{yelpTotal.toLocaleString()}</span> businesses on Yelp
                  <span className="text-gray-400 ml-2">(showing {yelpResults.length})</span>
                </p>
              </div>
            )}

            {/* Yelp Results Grid */}
            {yelpLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
              </div>
            ) : yelpResults.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {yelpResults.map((business) => (
                  <Card key={business.yelp_id} className="hover:shadow-lg transition-all group border-gray-200">
                    <CardContent className="p-0">
                      {/* Business Image */}
                      {business.image_url && (
                        <div className="h-40 overflow-hidden rounded-t-lg">
                          <img 
                            src={business.image_url} 
                            alt={business.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        </div>
                      )}
                      
                      {/* Business Info */}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-bold text-gray-800 text-lg">{business.name}</h3>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {business.city}, {business.state}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 text-xs">
                            Yelp
                          </Badge>
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                            <span className="font-semibold">{business.rating}</span>
                          </div>
                          <span className="text-sm text-gray-500">({business.review_count} reviews)</span>
                          {business.price && (
                            <span className="text-sm text-green-600 font-medium">{business.price}</span>
                          )}
                        </div>

                        {/* Categories */}
                        {business.categories?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {business.categories.slice(0, 3).map((cat, i) => (
                              <Badge key={i} variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                                {cat}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Phone */}
                        {business.phone && (
                          <p className="text-sm text-gray-600 mb-3 flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            {business.phone}
                          </p>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-3 border-t">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="flex-1"
                            onClick={() => window.open(business.url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Yelp
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
                            onClick={() => shareViaText(business)}
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            Text
                          </Button>
                          <Button 
                            size="sm"
                            className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                            onClick={() => inviteYelpBusiness(business)}
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Email
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : yelpLocation && !yelpLoading ? (
              <Card className="mb-6">
                <CardContent className="py-12 text-center">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Search for Barbers & Stylists</h3>
                  <p className="text-gray-400">Enter a city or location and click Search to find businesses on Yelp</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="mb-6">
                <CardContent className="py-12 text-center">
                  <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Search Anywhere in the US</h3>
                  <p className="text-gray-400 mb-4">Find barbers and stylists in any city, then invite them to join MyHairChair</p>
                  <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500">
                    <span className="bg-gray-100 px-3 py-1 rounded-full">New York</span>
                    <span className="bg-gray-100 px-3 py-1 rounded-full">Los Angeles</span>
                    <span className="bg-gray-100 px-3 py-1 rounded-full">Chicago</span>
                    <span className="bg-gray-100 px-3 py-1 rounded-full">Houston</span>
                    <span className="bg-gray-100 px-3 py-1 rounded-full">Miami</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* MyHairChair Results - Map & List Views */}
        {searchMode === "myhairchair" && (
          <>
            {/* Map View */}
            {viewMode === "map" && (
              <div className="mb-6">
                <Card className="overflow-hidden">
                  <div 
                    ref={mapContainer} 
                    className="w-full h-[500px] md:h-[600px]"
                    style={{ minHeight: '500px' }}
                  />
                </Card>
                <p className="text-sm text-gray-500 mt-2 text-center">
                  Click on a marker to view stylist details • {filteredStylists.filter(s => s.coordinates?.lat).length} stylists shown on map
                </p>
              </div>
            )}

            {/* List View */}
            {viewMode === "list" && (
              <>
                {filteredStylists.length === 0 ? (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-600 mb-2">No Stylists Found</h3>
                      <p className="text-gray-400">Try adjusting your search or filters</p>
                    </CardContent>
                  </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStylists.map((stylist) => (
                  <Card 
                    key={stylist.user_id} 
                    className={`hover:shadow-lg transition-all group ${
                      stylist.is_priority ? 'ring-2 ring-amber-400/50 shadow-amber-100' : ''
                    }`}
                  >
                    <CardContent className="p-0">
                      {/* Priority Badge for Pro/Elite */}
                      {stylist.badge && (
                        <div className={`px-3 py-1 text-center text-xs font-bold ${
                          stylist.badge === 'Elite' 
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' 
                            : 'bg-gradient-to-r from-amber-500 to-yellow-500 text-black'
                        }`}>
                          ⭐ {stylist.badge} Member
                        </div>
                      )}
                      
                      {/* Soldier Badge for HairChair Soldiers */}
                      {stylist.is_soldier && !stylist.badge && (
                        <div className="px-3 py-1 text-center text-xs font-bold bg-gradient-to-r from-zinc-800 to-zinc-900 text-amber-400 flex items-center justify-center gap-1">
                          <Shield className="w-3 h-3" />
                          HairChair Soldier
                        </div>
                      )}
                      
                      {/* Stylist Header */}
                      <div className="relative p-6 pb-4">
                        {/* Favorite Button */}
                        <button
                          onClick={() => toggleFavorite(stylist.user_id)}
                          className="absolute top-4 right-4 p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all z-10"
                        >
                          {favorites.includes(stylist.user_id) ? (
                            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                          ) : (
                            <Heart className="w-5 h-5 text-gray-400 group-hover:text-red-400" />
                          )}
                        </button>

                        <div className="flex items-start gap-4">
                          <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            stylist.badge === 'Elite' 
                              ? 'bg-gradient-to-br from-purple-500 to-purple-600' 
                              : stylist.badge === 'Pro'
                              ? 'bg-gradient-to-br from-amber-500 to-yellow-500'
                              : 'bg-gradient-to-br from-orange-500 to-amber-500'
                          }`}>
                            <User className="w-8 h-8 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-gray-800 text-lg truncate">
                                {stylist.user?.name || "Stylist"}
                              </h3>
                              {stylist.badge && (
                                <Badge className={`text-xs ${
                                  stylist.badge === 'Elite' 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {stylist.badge}
                                </Badge>
                              )}
                              {stylist.is_soldier && (
                                <Badge className="text-xs bg-zinc-900 text-amber-400 flex items-center gap-1" title="HairChair Soldier - Founding Partner">
                                  <Shield className="w-3 h-3" />
                                </Badge>
                              )}
                            </div>
                            <p className="text-orange-600 font-medium capitalize">
                              {stylist.professional_type || "Stylist"}
                            </p>
                            {(stylist.city || stylist.state) && (
                              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {[stylist.city, stylist.state].filter(Boolean).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Rating & Experience */}
                      <div className="px-6 pb-4">
                        <div className="flex items-center gap-4 text-sm">
                          {stylist.rating > 0 && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Star className="w-4 h-4 fill-amber-500" />
                              {stylist.rating?.toFixed(1)}
                              <span className="text-gray-400">({stylist.total_reviews || 0})</span>
                            </span>
                          )}
                          {stylist.experience_years > 0 && (
                            <span className="text-gray-500">
                              {stylist.experience_years} years exp.
                            </span>
                          )}
                        </div>

                        {/* Specialties */}
                        {stylist.specialties?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {stylist.specialties.slice(0, 3).map((spec, i) => (
                              <Badge key={i} variant="secondary" className="text-xs bg-orange-50 text-orange-700">
                                {spec}
                              </Badge>
                            ))}
                            {stylist.specialties.length > 3 && (
                              <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                                +{stylist.specialties.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Bio Preview */}
                        {stylist.bio && (
                          <p className="text-sm text-gray-500 mt-3 line-clamp-2">
                            {stylist.bio}
                          </p>
                        )}
                        
                        {/* Google Reviews Link */}
                        {stylist.google_business_url && (
                          <a 
                            href={stylist.google_business_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            View Google Reviews
                          </a>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="px-6 pb-6 pt-2 border-t bg-gray-50/50">
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => navigate(`/book-guest/${stylist.user_id}`)}
                          >
                            View Profile
                          </Button>
                          <Button 
                            className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                            onClick={() => navigate(`/book-guest/${stylist.user_id}`)}
                          >
                            <Calendar className="w-4 h-4 mr-1" />
                            Book
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
        </>
        )}
      </main>

      {/* Enhanced Beta Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="bg-stone-900 border-stone-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white sr-only">Invite to Beta</DialogTitle>
          </DialogHeader>
          <BetaInviteSystem user={user} onClose={() => setShowInviteModal(false)} />
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav userRole={user?.role || "guest"} />
    </div>
  );
};

export default FindStylists;
